/**
 * UC3 Relationships runs (Part B, 11 Oct 2026): the `local_bivariate` job, the kept runs
 * (at most three) and their flow layers. Called by `Uc3` itself, not by the step body, so a
 * run and a job still polling survive the reader moving to another step. See
 * `Uc3Relationships.tsx` for the step body and the Results stage's cards.
 *
 * NOTES #760: each run's map legend is written here, not taken from the server: the six
 * classes in reader order with their plain labels and hover sentences (`lbrClasses.ts`),
 * rewritten when the language changes.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useToolRun, type LbrFactor, type LocalBivariateResult, type ToolRun } from '../../lib/analysis';
import { useAppStore } from '../../lib/state';
import { lbrLegend, lbrSubject } from './lbrClasses';

export const LBR_FACTORS: readonly LbrFactor[] = ['ndvi', 'rain', 'soil'];
export const LBR_MAX_RUNS = 3;
export const LBR_DEFAULT_K = 30;
export const LBR_DEFAULT_PERMUTATIONS = 199;
export const LBR_MAX_PERMUTATIONS = 499;
/** UC3 crop ids with a GGCP10 production grid (sorghum and millet have none). */
export const LBR_CROPS = ['maize', 'rice', 'soy'] as const;

/** The flow step id the runs' layers are listed under. */
const STEP_ID = 'relationships';

export interface LbrRun {
  n: number;
  result: LocalBivariateResult;
  layerId: string;
}

export interface Uc3RelationshipsState {
  run: ToolRun<LocalBivariateResult>;
  runs: LbrRun[];
  replaced: { old: number; n: number } | null;
  submit: (params: Record<string, unknown>) => void;
  /**
   * NOTES #768: the factor of the run in flight and when it was submitted (ms epoch), for the
   * visible running state (`LbrRunning`); `null` before the first Run.
   */
  pending: { factor: LbrFactor; startedAt: number } | null;
}

const layerIdOf = (n: number) => `intel:uc3:lbr:${n}`;

/** A run's name, on the legend and its card: "Productivity vs vegetation, Burkina Faso, 2020". */
export function lbrRunName(
  t: (key: string, options?: Record<string, unknown>) => string,
  result: Pick<LocalBivariateResult, 'factor' | 'x_year' | 'unit'>,
): string {
  return t(`intel.uc3.lbr.layer.${result.factor}`, { year: result.x_year, unit: result.unit.name });
}

export function useUc3Relationships(): Uc3RelationshipsState {
  const { t, i18n } = useTranslation();
  const run = useToolRun<LocalBivariateResult>('local_bivariate');
  const addResultLayer = useAppStore((s) => s.addResultLayer);
  const setUc3StepLayers = useAppStore((s) => s.setUc3StepLayers);
  const [runs, setRuns] = useState<LbrRun[]>([]);
  const [replaced, setReplaced] = useState<{ old: number; n: number } | null>(null);
  const [pending, setPending] = useState<Uc3RelationshipsState['pending']>(null);
  const counter = useRef(0);
  const handled = useRef<LocalBivariateResult | null>(null);

  const labelOf = (result: LocalBivariateResult) => lbrRunName(t, result);
  const layerOf = (r: LbrRun, jobId: string) => {
    const spec = r.result.result_layers[0];
    if (!spec) return;
    addResultLayer(
      {
        ...spec,
        id: r.layerId,
        label: labelOf(r.result),
        legend: lbrLegend(t, lbrSubject(r.result.factor, r.result.soil_var)),
      },
      { jobId, tool: 'local_bivariate', label: labelOf(r.result) },
    );
  };

  useEffect(() => {
    const result = run.result;
    if (!result || run.phase !== 'done' || handled.current === result) return;
    handled.current = result;
    counter.current += 1;
    const n = counter.current;
    const layerId = layerIdOf(n);
    layerOf({ n, result, layerId }, run.jobId ?? `lbr-${n}`);
    const kept = runs.length >= LBR_MAX_RUNS ? runs.slice(runs.length - LBR_MAX_RUNS + 1) : runs;
    setReplaced(runs.length >= LBR_MAX_RUNS ? { old: runs[0]!.n, n } : null);
    const next = [...kept, { n, result, layerId }];
    setRuns(next);
    // Only the runs whose layer is still on the map (the legend's ✕ removes one), plus the new one.
    const onMap = new Set(useAppStore.getState().results.map((r) => r.id));
    setUc3StepLayers(
      STEP_ID,
      next.filter((r) => r.n === n || onMap.has(r.layerId)).map((r) => ({ layerId: r.layerId, label: labelOf(r.result) })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run.result, run.phase]);

  // Another language: the runs still on the map take its legend words and names.
  const lastLanguage = useRef(i18n.language);
  useEffect(() => {
    if (lastLanguage.current === i18n.language) return;
    lastLanguage.current = i18n.language;
    const results = useAppStore.getState().results;
    const onMap = runs.filter((r) => results.some((x) => x.id === r.layerId));
    onMap.forEach((r) => layerOf(r, results.find((x) => x.id === r.layerId)!.jobId));
    if (onMap.length) {
      setUc3StepLayers(STEP_ID, onMap.map((r) => ({ layerId: r.layerId, label: labelOf(r.result) })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const submit = (params: Record<string, unknown>) => {
    setPending({ factor: (params.factor as LbrFactor) ?? 'ndvi', startedAt: Date.now() });
    run.run(params);
  };

  return { run, runs, replaced, submit, pending };
}
